import { Typography } from '@mui/material'
import React, { Fragment, ChangeEvent, memo, useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import tw from 'twin.macro'
import { Formik, FormikProps } from 'formik'
import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { CombinedPIICollectionFormValues } from './PIICollection.interface'
import { getCombinedPIICollectionSchema } from './CombinedPIICollectionForm.validator'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { states } from 'src/constants/states'
import { TextBlockMajor, CircleAlertMajor } from '@shopify/polaris-icons'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { FormikRadioGroup } from '../shared/form/formik/FormikRadioGroup'
import { InfoContainer } from '../shared/InfoContainer'
import { CommonModal } from '../shared/CommonModal'
import workAuthDocExample from 'src/assets/images/work-auth-doc-example.png'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CombinedUploadSSN, SkipStep } from 'src/constants/actionTypes'
import { getDriver } from 'src/selectors/driver'
import { CombinedUploadSSNRequest } from 'src/interfaces/ssn'
import { usePersona } from 'src/hooks/usePersona'
import { PIICollectDataStep } from 'src/interfaces/collectData'
import { StepLabels } from '../client/step-labels/StepLabels'

const SSN_MASK = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

enum SSNFormType {
  SKIP_SSN = 'skip_ssn',
  PROVIDE_SSN = 'provide_ssn',
}

const ssnOptions = [
  { label: 'I want to proceed without SSN', value: SSNFormType.SKIP_SSN },
  { label: 'I want to provide SSN', value: SSNFormType.PROVIDE_SSN },
]

const workAuthorizationOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

const CombinedPIICollectionFormComponent = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as PIICollectDataStep
  const driver = useSelector(getDriver)

  const formikRef = useRef<FormikProps<CombinedPIICollectionFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showWorkAuthDocInfoModal, setShowWorkAuthDocInfoModal] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [currentFormType, setCurrentFormType] = useState<SSNFormType | null>(null)

  const { savedValues, onValuesChange, clearStorage } =
    useStepFormStorage<CombinedPIICollectionFormValues>({
      storageKey: `step_pii_form_${currentStep?.id}`,
      fileFields: ['ssnFile', 'workAuthFile', 'ssn'],
    })

  const handlePersonaError = useCallback(() => {
    setError('Failed to complete persona verification. Please contact support.')
  }, [])

  const { startPersona } = usePersona({
    onError: handlePersonaError,
  })

  const handleSSNTypeChange = (_event: ChangeEvent<HTMLInputElement>, value: string | number) => {
    setCurrentFormType(value as SSNFormType)
  }

  const handleSkipStep = useCallback(async () => {
    if (!currentStep?.id) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const requestBody = {
        stepId: currentStep.id,
      }

      await createAsyncAction(dispatch, SkipStep.request(requestBody))
    } catch (e: any) {
      setError(e?.message || 'Failed to skip step')
    } finally {
      setIsLoading(false)
    }
  }, [currentStep?.id, dispatch])

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedOption = workAuthorizationOptions.find((option) => {
      return String(option.value) === event.target.value
    })

    if (!selectedOption) {
      return
    }

    const requiresWorkAuthDoc = selectedOption.value

    formikRef.current?.setFieldValue('workAuthorization', requiresWorkAuthDoc)

    if (!requiresWorkAuthDoc) {
      formikRef.current?.setFieldValue('workAuthFile', null)
    }
  }

  const handleShowWorkAuthDocInfo = () => {
    setShowWorkAuthDocInfoModal(true)
  }

  const closeWorkAuthDocInfoModal = () => {
    setShowWorkAuthDocInfoModal(false)
  }

  const handleSubmit = useCallback(
    async (values: CombinedPIICollectionFormValues) => {
      if (!currentStep?.id) {
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        const requestBody: CombinedUploadSSNRequest = {
          address: {
            streetAddress: values.streetAddress,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
          },
          ssn: values.ssn.replace(/\D/g, ''),
          stepId: currentStep.id,
          ssnFile: values.ssnFile as File,
        }

        if (values.workAuthorization && values.workAuthFile) {
          requestBody.workAuthFile = values.workAuthFile
          requestBody.workAuthorization = values.workAuthorization
        }

        await createAsyncAction(dispatch, CombinedUploadSSN.request(requestBody))

        clearStorage()

        await startPersona(currentStep.id)
      } catch (e: any) {
        setError(e?.message || 'Failed to start SSN verification')
      } finally {
        setIsLoading(false)
      }
    },
    [currentStep?.id, startPersona],
  )

  const getFormInitialValues = (): CombinedPIICollectionFormValues => {
    const baseValues: CombinedPIICollectionFormValues = {
      ssn: '',
      ssnFile: null,
      workAuthFile: null,
      workAuthorization: false,
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
    }

    if (driver) {
      const streetAddress = driver.addressStreet1 || driver.addressStreet2 || ''

      baseValues.streetAddress = streetAddress
      baseValues.city = driver.addressCity ?? ''
      baseValues.state = driver.addressState ?? ''
      baseValues.zipCode = driver.addressPostalCode ?? ''
    }

    if (savedValues) {
      return {
        ...baseValues,
        streetAddress: savedValues.streetAddress || baseValues.streetAddress,
        city: savedValues.city || baseValues.city,
        state: savedValues.state || baseValues.state,
        zipCode: savedValues.zipCode || baseValues.zipCode,
        workAuthorization: savedValues.workAuthorization ?? baseValues.workAuthorization,
      }
    }

    return baseValues
  }

  const isStepOptional = currentStep?.optional

  return (
    <Formik
      initialValues={getFormInitialValues()}
      onSubmit={handleSubmit}
      validationSchema={getCombinedPIICollectionSchema(
        !currentStep?.metadata?.requireWorkAuthorizationProof,
      )}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange, values }) => {
        onValuesChange(values)

        return (
          <div css={tw`h-full w-full flex flex-col`}>
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <TextBlockMajor height={20} width={20} />
              </div>
              <StepLabels />
              <Typography css={tw`my-2`} variant="h2">
                Attach Your SSN Documents For Verification
              </Typography>
              <Typography css={tw`mb-2 text-shades-400`}>
                Before proceeding with Persona please upload your SSN documents for review.
              </Typography>

              {isStepOptional && (
                <div css={tw`mt-4`}>
                  <Typography
                    css={tw`text-shades-400 text-xs font-bold uppercase`}
                    variant="overline"
                  >
                    Select preferable option
                  </Typography>
                  <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5`}>
                    <div css={tw`p-4 rounded-lg flex flex-col mb-4 bg-info-200`}>
                      <div css={tw`flex flex-row items-center mb-1`}>
                        <div css={tw`w-5 h-5 mr-2`}>
                          <CircleAlertMajor width={20} height={20} css={tw`fill-info-500`} />
                        </div>
                        <Typography variant="h6" css={tw`text-info-500`}>
                          SSN Not Required
                        </Typography>
                      </div>
                      <Typography variant="body2" css={tw`text-info-500`}>
                        You've already provided your business entity details, so you can continue
                        without entering an SSN.
                      </Typography>
                    </div>
                    <BasicRadioGroup
                      name="ssnType"
                      options={ssnOptions}
                      value={currentFormType ?? ''}
                      onChange={handleSSNTypeChange}
                      dataTestId="contractors-onboarding-pii-ssn-option-input"
                    />
                  </div>
                </div>
              )}

              {(!isStepOptional || currentFormType === SSNFormType.PROVIDE_SSN) && (
                <Fragment>
                  <div css={tw`mt-6`}>
                    {currentStep?.metadata?.requireWorkAuthorizationProof && (
                      <Typography
                        css={tw`text-shades-400 text-xs font-bold uppercase`}
                        variant="overline"
                      >
                        SSN document
                      </Typography>
                    )}

                    <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                      <div css={tw`w-full mb-0`}>
                        <FormikTextField
                          name="ssn"
                          placeholder="Enter SSN"
                          label="Social security number"
                          mask={SSN_MASK}
                          onChange={handleChange}
                          css={tw`w-full`}
                          inputProps={{ 'data-testid': 'contractors-onboarding-pii-ssn-input' }}
                        />
                      </div>
                      {currentStep?.metadata?.requireWorkAuthorizationProof && (
                        <div css={tw`mt-6`}>
                          <Typography css={tw`mb-1.5 text-shades-400`}>
                            Upload a photo of your Social Security Card, or a photo of your U.S.
                            Birth Certificate, or a photo of the photo page of a U.S. Passport.
                          </Typography>
                          <FormikFileInput
                            name="ssnFile"
                            inputDataTestId="contractors-onboarding-pii-ssn-file-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {currentStep?.metadata?.requireWorkAuthorizationProof && (
                    <div css={tw`mt-6`}>
                      <Typography
                        css={tw`text-shades-400 text-xs font-bold uppercase`}
                        variant="overline"
                      >
                        Work Authorization Document (Optional)
                      </Typography>

                      <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                        <InfoContainer
                          onClick={handleShowWorkAuthDocInfo}
                          dataTestId="contractors-onboarding-pii-work-auth-info-btn"
                        >
                          Learn more about Work Authorization Document
                        </InfoContainer>
                        <div css={tw`w-full mb-4 mt-4`}>
                          <FormikRadioGroup
                            label="Would your SSN only be valid for work with an EAD (Employment Authorization Document) or RP (Permanent Resident / Green Card)?"
                            name="workAuthorization"
                            options={workAuthorizationOptions}
                            onChange={handleOptionChange}
                            dataTestId="contractors-onboarding-pii-work-authorization-input"
                          />
                        </div>
                        {values.workAuthorization && (
                          <div css={tw`mt-6`}>
                            <Typography css={tw`mb-1.5 text-shades-400`}>
                              Attach your Employment Authorization (EAD) or Resident Permit (RP)
                            </Typography>
                            <FormikFileInput
                              name="workAuthFile"
                              inputDataTestId="contractors-onboarding-pii-work-auth-file-input"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div css={tw`mt-6`}>
                    <Typography
                      css={tw`text-shades-400 text-xs font-bold uppercase`}
                      variant="overline"
                    >
                      Your Address
                    </Typography>
                  </div>
                  <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                    <div css={tw`w-full mb-4`}>
                      <FormikTextField
                        name="streetAddress"
                        placeholder="Enter address"
                        label="Street address"
                        onChange={handleChange}
                        css={tw`w-full`}
                        inputProps={{ 'data-testid': 'contractors-onboarding-pii-street-input' }}
                      />
                    </div>
                    <div css={tw`w-full mb-4`}>
                      <FormikTextField
                        name="city"
                        placeholder="Enter city"
                        label="City"
                        onChange={handleChange}
                        css={tw`w-full`}
                        inputProps={{ 'data-testid': 'contractors-onboarding-pii-city-input' }}
                      />
                    </div>
                    <div css={tw`mb-4`}>
                      <FormikSelect
                        name="state"
                        label="State"
                        placeholder="Select state"
                        options={states}
                        onChange={handleChange}
                        inputProps={{ 'data-testid': 'contractors-onboarding-pii-state-input' }}
                      />
                    </div>
                    <div css={tw`w-full mb-4`}>
                      <FormikTextField
                        name="zipCode"
                        placeholder="Enter zip code"
                        label="Zip code"
                        onChange={handleChange}
                        css={tw`w-full`}
                        inputProps={{ 'data-testid': 'contractors-onboarding-pii-zip-input' }}
                      />
                    </div>
                  </div>
                  <Button
                    className={tw`w-full mt-6`}
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                    data-testid="contractors-onboarding-pii-submit-btn"
                  >
                    Submit and Continue
                  </Button>
                </Fragment>
              )}

              {isStepOptional && currentFormType === SSNFormType.SKIP_SSN && (
                <Button
                  className={tw`w-full mt-6`}
                  loading={isLoading}
                  onClick={handleSkipStep}
                  data-testid="contractors-onboarding-pii-skip-continue-btn"
                >
                  Continue
                </Button>
              )}
            </div>
            <FormErrorMessage css={tw`my-4`} error={error} />
            <CommonModal
              title="About Work Authorization Document"
              isOpen={showWorkAuthDocInfoModal}
              onClose={closeWorkAuthDocInfoModal}
              closeButtonTestId="contractors-onboarding-pii-work-auth-modal-close-btn"
            >
              <div css={tw`mb-0 pl-6 pt-2`}>
                <ul css={tw`list-disc space-y-2 text-shades-400 m-0 pl-0`}>
                  <li>
                    If it says 'VALID FOR WORK ONLY WITH DHS AUTHORIZATION', you'll need to upload
                    your EAD or Green Card
                  </li>
                  <li>If it does not have this note, no additional document is required</li>
                </ul>
              </div>
              <img css={tw`mt-4 mb-6 w-full rounded-xl`} src={workAuthDocExample} />
            </CommonModal>
          </div>
        )
      }}
    </Formik>
  )
}

export const CombinedPIICollectionForm = memo(CombinedPIICollectionFormComponent)
